/**
 * Who may open what in the console (release 3.1, persona work package D6).
 *
 * ONE rule, from ONE table: `decidePage` in the generated persona table
 * (src/generated/persona/discovery-access.ts, from the persona specification
 * and the controller's route gates). A page is offered when the specification
 * shows it to the caller's role, the controller grants the caller every
 * permission the page's data calls need (GET /v1/me), and the tenant holds a
 * licence for one of its modules (GET /v1/entitlements/current). The sidebar,
 * the route guard, the role Home and the top bar all ask here; none of them
 * keeps a role list of its own.
 *
 * FAIL CLOSED. Until both answers arrive, nothing is offered and no guarded
 * page renders. If GET /v1/me fails, nothing is offered. If the licences
 * cannot be read, no licensed page is offered (`licence_unknown`).
 */
import { useCallback, useEffect, useState } from 'react';

import {
  builtInRoleOf,
  decidePage,
  findPage,
  pageById,
  TARGET_PERMISSIONS,
  type Caller,
  type PageDecision,
  type PageEntry,
} from '@/generated/persona/discovery-access';
import { apiFetch, getAuthToken } from './api';
import { useEntitlements, type Entitlements } from './use-entitlements';

/** GET /v1/me (apps/api-controller/src/modules/me/routes.ts). */
export interface Me {
  tenantId: string;
  actorId: string;
  role: string;
  builtInRole: string | null;
  roleLabel: string;
  customRoleId: string | null;
  platformSession: boolean;
  businessUnits: { all: boolean; allowed: string[] };
  permissions: string[];
}

let cache: { token: string; me: Me } | null = null;
let inflight: { token: string; promise: Promise<Me> } | null = null;

/** Test hook: forget the cached answer. */
export function resetMeCache(): void {
  cache = null;
  inflight = null;
}

function loadMe(token: string): Promise<Me> {
  if (cache && cache.token === token) return Promise.resolve(cache.me);
  if (inflight && inflight.token === token) return inflight.promise;
  const promise = apiFetch<Me>('/v1/me').then(
    (me) => {
      cache = { token, me };
      inflight = null;
      return me;
    },
    (err: unknown) => {
      inflight = null;
      throw err;
    },
  );
  inflight = { token, promise };
  return promise;
}

interface MeState {
  loading: boolean;
  me: Me | null;
  error: string | null;
}

/** The caller's GET /v1/me, re-read when the session changes. */
export function useMe(): MeState & { refresh: () => void } {
  const [state, setState] = useState<MeState>(() => {
    const token = getAuthToken();
    return token && cache && cache.token === token
      ? { loading: false, me: cache.me, error: null }
      : { loading: token !== null, me: null, error: null };
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onChange = (): void => setTick((t) => t + 1);
    window.addEventListener('foctta:auth-changed', onChange);
    return () => window.removeEventListener('foctta:auth-changed', onChange);
  }, []);

  useEffect(() => {
    let live = true;
    const token = getAuthToken();
    if (!token) {
      setState({ loading: false, me: null, error: null });
      return;
    }
    if (cache && cache.token === token) {
      setState({ loading: false, me: cache.me, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    loadMe(token).then(
      (me) => live && setState({ loading: false, me, error: null }),
      (err: unknown) =>
        live && setState({ loading: false, me: null, error: err instanceof Error ? err.message : 'could not read your access' }),
    );
    return () => {
      live = false;
    };
  }, [tick]);

  const refresh = useCallback(() => {
    cache = null;
    setTick((t) => t + 1);
  }, []);
  return { ...state, refresh };
}

/**
 * The modules a page may need that the tenant holds: licensed ones, active
 * trials; none when billing is cancelled. Null when the licences are unknown.
 */
export function licensedModules(entitlements: Entitlements | null, failed: boolean): Set<string> | null {
  if (failed || !entitlements) return null;
  if (entitlements.billingStatus === 'cancelled') return new Set();
  const out = new Set(entitlements.modules);
  const trialLive = entitlements.trialEndsAt !== null && new Date(entitlements.trialEndsAt) > new Date();
  if (trialLive) for (const m of entitlements.trialModules) out.add(m);
  return out;
}

export type CallerState =
  | { status: 'loading'; caller: null; me: null }
  | { status: 'error'; caller: null; me: null; error: string }
  | { status: 'ready'; caller: Caller; me: Me };

/** The caller as the persona table's `decidePage` needs it, once both answers are in. */
export function useCaller(): CallerState & { retry: () => void } {
  const meState = useMe();
  const ent = useEntitlements();
  const retry = useCallback(() => {
    meState.refresh();
    void ent.refresh();
  }, [meState, ent]);
  if (meState.error) return { status: 'error', caller: null, me: null, error: meState.error, retry };
  if (meState.loading || ent.loading || !meState.me) return { status: 'loading', caller: null, me: null, retry };
  const caller: Caller = {
    role: meState.me.role,
    permissions: new Set(meState.me.permissions),
    modules: licensedModules(ent.entitlements, ent.error !== null),
  };
  return { status: 'ready', caller, me: meState.me, retry };
}

/** May `caller` open the page with this specification id? Unknown ids: no. */
export function canOpen(pageId: string, caller: Caller | null): boolean {
  if (!caller) return false;
  const page = pageById(pageId);
  return page ? decidePage(page, caller).allowed : false;
}

/**
 * The decision for a router path. `undefined` when the path is not a page of
 * the specification (the router's not-found or a redirect route handles it).
 * The super-admin console is for FOCTTA staff on a platform-issued session
 * only (shared/platform-session.ts on the controller).
 */
export function decidePath(
  pathname: string,
  caller: Caller,
  me: Me,
): { page: PageEntry; decision: PageDecision } | undefined {
  const page = findPage(pathname);
  if (!page) return undefined;
  if (page.id === 'disc.super_admin') {
    return {
      page,
      decision: me.platformSession ? { allowed: true } : { allowed: false, reason: 'role', missing: [] },
    };
  }
  return { page, decision: decidePage(page, caller) };
}

/**
 * Does the caller hold `permission` (GET /v1/me)? False while unknown. For a
 * control whose route needs more than its page does (release 3.1, D1: the
 * Section 9 records, the erasure execute button, the classify test panel).
 */
export function useHolds(permission: string): boolean {
  const access = useCaller();
  return access.status === 'ready' && access.caller.permissions.has(permission);
}

/**
 * May the caller take an action that needs `permission`? The controller must
 * grant it (GET /v1/me) AND, for a built-in role, the persona specification's
 * target table must give it to that role: a button the specification removed
 * is not offered even while the API still allows it (api-gaps.json lists
 * those for the work package that closes them). A custom role: its grants.
 * Same rule as Consent's C5 (`can`).
 */
export function canAct(permission: string, caller: Caller | null): boolean {
  if (!caller || !caller.permissions.has(permission)) return false;
  const role = builtInRoleOf(caller.role);
  const holders = TARGET_PERMISSIONS[permission];
  if (!role || !holders) return true;
  return holders.includes(role);
}

/** `canAct` for the current caller. False while unknown. */
export function useCan(permission: string): boolean {
  const access = useCaller();
  return access.status === 'ready' && canAct(permission, access.caller);
}
